import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, AlertCircle, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';

export type ToastTone = 'success' | 'error';

interface Toast {
  id: number;
  message: string;
  tone: ToastTone;
}

interface ToastApi {
  /** Confirms a mutation the user just triggered. Auto-dismisses. */
  success: (message: string) => void;
  /** For a failure with nowhere better to go. A form error belongs in its form, not here. */
  error: (message: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

// 6 s: the provisioning notices name a service desk and a fallback, which is more than four
// seconds of reading for a message that never repeats.
const AUTO_DISMISS_MS = 6000;

/**
 * Minimal toast, deliberately hand-rolled: no toast library is installed and adding one for a
 * strip of text would be a dependency for eighty lines. Announced politely rather than
 * assertively — a confirmation must not interrupt a screen reader mid-sentence.
 *
 * A toast is a bonus, never the only feedback: a failed mutation still shows its message inside
 * the dialog that caused it, which stays open.
 */
export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(0);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const push = useCallback(
    (message: string, tone: ToastTone) => {
      nextId.current += 1;
      const id = nextId.current;
      setToasts((current) => [...current, { id, message, tone }]);
      // Timer is not cleared on unmount on purpose: dismiss() on an unmounted provider is a
      // no-op setState React tolerates, and tracking timers here would outweigh the risk.
      setTimeout(() => dismiss(id), AUTO_DISMISS_MS);
    },
    [dismiss]
  );

  const api = useMemo<ToastApi>(
    () => ({
      success: (message: string) => push(message, 'success'),
      error: (message: string) => push(message, 'error'),
    }),
    [push]
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
};

const ToastViewport = ({ toasts, onDismiss }: { toasts: Toast[]; onDismiss: (id: number) => void }) => {
  const { t } = useTranslation();

  // Portaled out of the Radix Themes root, whose stacking context kept z-100 under the drawer's
  // z-50. The pointerdown stays here, so a modal drawer does not read the ✕ as an outside click.
  return createPortal(
    <div
      role="status"
      aria-live="polite"
      onPointerDown={(event) => event.stopPropagation()}
      className="pointer-events-none fixed bottom-4 right-4 z-[100] flex w-full max-w-sm flex-col gap-2"
    >
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={cn(
            'pointer-events-auto flex items-start gap-2 rounded-lg border px-3 py-2 text-sm shadow-lg',
            toast.tone === 'success'
              ? 'border-emerald-200 bg-white text-emerald-900 dark:border-emerald-900/50 dark:bg-zinc-900 dark:text-emerald-200'
              : 'border-red-200 bg-white text-red-900 dark:border-red-900/50 dark:bg-zinc-900 dark:text-red-200'
          )}
        >
          {toast.tone === 'success' ? (
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" aria-hidden="true" />
          ) : (
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" aria-hidden="true" />
          )}
          <span className="flex-1">{toast.message}</span>
          <button
            type="button"
            onClick={() => onDismiss(toast.id)}
            aria-label={t('Dismiss')}
            className="shrink-0 rounded p-0.5 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ))}
    </div>,
    document.body
  );
};

/**
 * Returns a no-op API outside a provider rather than throwing: a missing toast must never be the
 * thing that takes a screen down, and it lets a component be unit-tested without a wrapper.
 */
const NO_OP: ToastApi = { success: () => {}, error: () => {} };

export const useToast = (): ToastApi => useContext(ToastContext) ?? NO_OP;
